import { BookOpen, ChevronRight, Clock } from 'lucide-react';
import { useState } from 'react';

import { Badge, cn } from '@workspace/shadcn';

interface Module {
  title: string;
  lessons: { title: string; description?: string | null; duration: string }[];
}

export function CourseModulesTabs({ modules }: { modules: Module[] }) {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedLessons, setExpandedLessons] = useState<number[]>([]);

  const toggleLessons = (index: number) => {
    setExpandedLessons((previous) =>
      previous.includes(index)
        ? previous.filter((item) => item !== index)
        : [...previous, index],
    );
  };

  if (modules.length === 0) return null;

  const active = modules[Math.min(activeTab, modules.length - 1)];

  return (
    <div className="border-border bg-background mb-8 flex w-full flex-col rounded-lg border lg:min-h-[600px]">
      <div className="border-border border-b p-6">
        <h2 className="text-primary text-2xl font-semibold">Course Content</h2>
        <p className="text-muted-foreground mt-2 text-sm">
          {modules.length} modules &bull;{' '}
          {modules.reduce((total, module) => total + module.lessons.length, 0)}{' '}
          lessons
        </p>
      </div>

      <div className="h-full grow lg:flex">
        <div className="lg:border-border lg:w-1/3 lg:border-e">
          <div className="max-h-[300px] overflow-y-auto lg:max-h-[500px]">
            {modules.map((module, index) => (
              <button
                key={module.title + index}
                type="button"
                className={cn(
                  'border-border w-full border-b px-6 py-4 text-start transition-colors',
                  activeTab === index
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted/80',
                )}
                onClick={() => {
                  setActiveTab(index);
                  setExpandedLessons([]);
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{module.title}</span>
                  <Badge variant="outline">{module.lessons.length}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:w-2/3">
          <div className="max-h-[300px] overflow-y-auto p-6 lg:max-h-[500px]">
            <h3 className="text-primary mb-4 text-xl font-semibold">
              {active.title}
            </h3>
            <ul className="space-y-3">
              {active.lessons.map((lesson, index) => (
                <li
                  key={lesson.title + index}
                  className="border-border hover:bg-muted/50 rounded-lg border transition-colors"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-3 text-start"
                    onClick={() => toggleLessons(index)}
                  >
                    <div className="flex items-center gap-3">
                      <BookOpen className="text-primary h-5 w-5 shrink-0" />
                      <span className="font-medium">{lesson.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground flex items-center text-sm whitespace-nowrap">
                        <Clock className="me-1 h-4 w-4" />
                        {lesson.duration}
                      </span>
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 transition-transform',
                          expandedLessons.includes(index) && 'rotate-90',
                        )}
                      />
                    </div>
                  </button>
                  {expandedLessons.includes(index) && (
                    <div className="border-border bg-muted/30 border-t p-3">
                      <p className="text-muted-foreground text-sm">
                        {lesson.description}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
